import React, { useState, useEffect } from 'react';
import '.././styles/HighRiskCases.css';
import { getHighRiskPregnancies } from '../services/authService';
import { useSelector } from 'react-redux';
// Utility function to calculate pregnancy week
const calculatePregnancyWeek = (lmpDate) => {
  const lmp = new Date(lmpDate);
  const today = new Date();
  const diffTime = Math.abs(today - lmp);
  const diffWeeks = Math.floor(diffTime / (1000 * 60 * 60 * 24 * 7));
  return diffWeeks;
};

// Utility function to format date
const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const HighRiskCases = () => {
  const [data, setData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [riskFilter, setRiskFilter] = useState('All Risk Levels');
  const [villageFilter, setVillageFilter] = useState('All Villages');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 10;
  const selectedVillageId = useSelector((state) => state.village.selectedVillageId);
  // Fetch data from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getHighRiskPregnancies(page ,limit);
        const result = response; 
        if (result.status && result.data.status) { 
          setData(result.data.data);
          setTotalPages(result.data.pagination.totalPages);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, [page, selectedVillageId]);

  // Handle search and filters
  const filteredData = data.filter((item) => {
    const matchesSearch = item['Beneficiary.name'].toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRisk = riskFilter === 'All Risk Levels' || item.risk_status === riskFilter.toLowerCase();
    console.log('item.risk_status',item.risk_status);
       console.log('riskFilter.toLowerCase()',riskFilter.toLowerCase());
    const matchesVillage = villageFilter === 'All Villages' || item['Beneficiary.Village.village_name'] === villageFilter;
    return matchesSearch && matchesRisk && matchesVillage;
  });

  // Handle pagination
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  return (
    <>
      <div className="info-banner">
        <span className="material-icons icon">warning</span>
        <div>
          <strong>High Risk Pregnancies Require Special Attention</strong>
          <p style={{ margin: '4px 0 0 0' }}>
            These beneficiaries need more frequent visits and potential referrals to healthcare facilities. Please prioritize follow-ups based on risk factors.
          </p>
        </div>
      </div>

      <div className="search-filters">
        <div className="search-field">
          <span className="material-icons search-icon">search</span>
          <input
            type="text"
            className="search-input"
            placeholder="Search by name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <select
          className="filter-dropdown"
          value={riskFilter}
          onChange={(e) => setRiskFilter(e.target.value)}
        >
          <option>All Risk Levels</option>
          <option>high_risk</option>
          <option>Normal</option>
        </select>
        <select
          className="filter-dropdown"
          value={villageFilter}
          onChange={(e) => setVillageFilter(e.target.value)}
        >
          <option>All Villages</option>
          <option>Ganeshpur</option>
        </select>
      </div>

      <div className="risk-card-grid">
        {filteredData.map((item) => (
          <div className="risk-card" key={item.tracking_id}>
            <div className="risk-card-header">
              <div className="risk-level">
                <span className="material-icons icon">warning</span>
                <span className="risk-level-text">
                  {item.risk_status === 'high_risk' ? 'High Risk' : 'Normal Risk'}
                </span>
              </div>
              <div className="edd">EDD: {formatDate(item.edd)}</div>
            </div>

            <div className="beneficiary-section">
              <div className="beneficiary-avatar">
                <span className="material-icons">pregnant_woman</span>
              </div>
              <div>
                <div className="beneficiary-name">{item['Beneficiary.name']}</div>
                <div className="beneficiary-details">{item['Beneficiary.age']} years • {item['Beneficiary.Village.village_name']}</div>
                <div className="beneficiary-details">Week {calculatePregnancyWeek(item.lmp_date)}</div>
              </div>
            </div>

            <div className="risk-factors">
              <div className="risk-factors-title">
                <span className="material-icons icon">error_outline</span>
                Risk Factors
              </div>
              <div>
                {item.risk_factors?.split(',').map((factor, index) => (
                  <span className="risk-tag" key={index}>{factor.trim()}</span>
                ))}
              </div>
            </div>

            <div className="anc-progress">
              <div className="anc-progress-title">
                <span className="material-icons icon">event_available</span>
                ANC Progress
              </div>
              <div className="progress-bar-container">
                <div
                  className="progress-bar"
                  style={{ width: `${Math.min(calculatePregnancyWeek(item.lmp_date) / 40 * 100, 100)}%` }}
                ></div>
              </div>
              <div className="progress-stats">
                <div>Week {calculatePregnancyWeek(item.lmp_date)}</div>
                <div>Due Date: {formatDate(item.edd)}</div>
              </div>
              <div className="anc-visits">
                {[...Array(5)].map((_, index) => (
                  <div
                    key={index}
                    className={`visit-dot ${index < item.anc_visit_count ? 'completed' : ''}`}
                  >
                    {index + 1}
                  </div>
                ))}
              </div>
              <div className="beneficiary-details" style={{ marginTop: '8px' }}>
                Next Visit: {item.anc_visit_count < 5 ? `ANC ${item.anc_visit_count + 1}` : 'N/A'}
              </div>
            </div>

            <div className="action-buttons">
              <button className="action-button primary">
                <span className="material-icons icon">add_circle</span>
                Record Visit
              </button>
              <button className="action-button secondary">
                <span className="material-icons icon">local_hospital</span>
                Refer
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="pagination">
        <div
          className="page-button"
          onClick={() => handlePageChange(page - 1)}
        >
          <span className="material-icons">chevron_left</span>
        </div>
        {[...Array(totalPages)].map((_, index) => (
          <div
            key={index}
            className={`page-button ${page === index + 1 ? 'active' : ''}`}
            onClick={() => handlePageChange(index + 1)}
          >
            {index + 1}
          </div>
        ))}
        <div
          className="page-button"
          onClick={() => handlePageChange(page + 1)}
        >
          <span className="material-icons">chevron_right</span>
        </div>
      </div>
    </>
  );
};

export default HighRiskCases;  